import { useLayoutEffect } from 'react';
import { useManageContext } from '../../../contexts/ManageContext/ManageContext';
import {
  Paper,
  Typography,
  makeStyles,
  Button,
  LinearProgress,
  Theme,
  Grid,
  Chip,
} from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuthUser } from '../../../contexts/AuthProvider/hooks';
import Visible from '../../../components/Visible/Visible';
import { isDarkTheme } from '../../../eliot-components/src/theme';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    width: '125px',
    display: 'inline-block',
  },
  details: {
    marginBottom: '1em',
  },
  archived: {
    backgroundColor: theme.palette.secondary.light,
  },
  selected: {
    backgroundColor: theme.palette.background.default,
  },
  btn: {
    marginRight: '5px',
  },
  summary: {
    height: '56px',
    borderTop: '1px',
    borderColor: theme.palette.text.primary,
    backgroundColor: isDarkTheme(theme)
      ? theme.palette.primary.main
      : '#eeeeee',
    [theme.breakpoints.down('lg')]: {
      marginTop: '30px',
    },
  },
  summaryItem: {
    padding: '0 15px',
  },
  noresults: {
    [theme.breakpoints.down('md')]: {
      marginTop: '45px',
    },
  },
  resultList: {
    marginTop: '10px',
  },
  searchNotFullySupported: {
    [theme.breakpoints.down('md')]: {
      marginTop: '45px',
      marginBottom: '-25px',
    },
  },
}));

const SearchResults = () => {
  const { state, dispatch } = useManageContext();
  const {
    devices: data,
    organizations,
    loadingDevices,
    searchQuery,
    advancedSearchAlert,
    searchError,
    userOrgs,
  } = state;
  let devices = data ? data.devices : [];
  if (userOrgs && devices) {
    devices.forEach((element: any, i: any) => {
      const org = userOrgs.filter((org: any) => {
        return element.owner === org.id;
      });
      if (org.length > 0) {
        devices[i].disabled = org[0].deactivated;
      }
    });
  }

  const more = data ? data.more : false;

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { selectOrgs } = useAuthUser();

  const actionSelect = (item: string[]) => {
    let q = '?archived=true';
    item.forEach((val) => {
      if (!val) {
        return;
      }
      if (q) {
        q += '&serial_number_or_imei=' + val;
      } else {
        q = 'serial_number_or_imei=' + val;
      }
    });
    if (q.length > 0) {
      const uri = '/devices' + q;
      history.push(uri);
      location.pathname = uri;
      dispatch({
        type: 'search-closed',
      });
    }
  };

  const actionDetails = (item: any) => {
    selectOrgs([item.owner]);
    history.push(`/devices/${encodeURIComponent(item.id)}`);
    dispatch({
      type: 'device-selected',
      data: item,
    });
  };

  const actionDashboard = (item: string[]) => {
    let q = '?archived=true';
    item.forEach((val) => {
      if (!val) {
        return;
      }
      if (q) {
        q += '&serial_number_or_imei=' + val;
      } else {
        q = 'serial_number_or_imei=' + val;
      }
    });

    if (q.length > 0) {
      const uri = '/dashboard' + q;
      history.push(uri);
      location.pathname = uri;
      dispatch({
        type: 'search-closed',
      });
    }
  };

  useLayoutEffect(() => {
    const src = 'search-results';
    document.body.classList.add(src);

    return () => {
      document.body.classList.remove(src);
    };
  }, []);

  let validQueryLength = true;
  for (const q of searchQuery || []) {
    if (q.length < 4) {
      validQueryLength = false;
    }
  }

  if (searchQuery && !validQueryLength) {
    return <div>Search query must be at least four characters long</div>;
  }
  if (loadingDevices) {
    return (
      <Paper
        className={classes.resultList}
        style={{ padding: '1em', marginBottom: '1em' }}
      >
        <div style={{ padding: '3em', textAlign: 'center' }}>
          Loading results
        </div>
        <LinearProgress />
      </Paper>
    );
  }

  if (devices && devices.length > 0) {
    const view = devices.map((item: any) => {
      return (
        <Paper
          key={item.id}
          style={{ padding: '1em', marginBottom: '1em', position: 'relative' }}
        >
          <Typography component="h2" variant="h6">
            {item.properties && item.properties.model
              ? item.properties.model
              : item.model}
          </Typography>
          <div
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              display: 'flex',
              gap: '8px',
            }}
          >
            {item.isArchived && (
              <Chip label="Archived" variant="outlined" color="primary" />
            )}
            {item.deactivated && (
              <Chip label="Blocked" variant="outlined" color="primary" />
            )}
          </div>
          <div className={classes.details}>
            <div>
              <span className={classes.field}>Organization:</span>{' '}
              {item.disabled ? (
                <i>
                  {organizations && organizations[item.owner]}{' '}
                  <span style={{ color: '#f44336' }}>&lt;deactivated&gt;</span>
                </i>
              ) : (
                organizations && organizations[item.owner]
              )}
            </div>
            <div>
              <span className={classes.field}>Serial number:</span>{' '}
              {item.serialNumber}
            </div>
            <div>
              <span className={classes.field}>IMEI:</span> {item.imei}
            </div>

            {item.mac && (
              <div>
                <span className={classes.field}>MAC:</span> {item.mac}
              </div>
            )}
          </div>
          {!item.disabled && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => actionDetails(item)}
            >
              Details
            </Button>
          )}
        </Paper>
      );
    });

    return (
      <div style={{ marginBottom: '100px' }}>
        <Visible when={advancedSearchAlert && more}>
          <Typography
            align="center"
            component="div"
            variant="h6"
            className={classes.searchNotFullySupported}
          >
            Search criteria not fully supported; results may be omitted
          </Typography>
        </Visible>
        <Visible when={searchError !== null}>
          <Typography
            align="center"
            component="div"
            variant="h6"
            className={classes.searchNotFullySupported}
          >
            {searchError}
          </Typography>
        </Visible>
        <div data-testid="result-list" className={classes.resultList}>
          {devices && devices.length && searchQuery && searchQuery.length && (
            <Grid
              container
              className={classes.summary}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid item>
                <Button
                  variant="contained"
                  className={classes.btn}
                  color="secondary"
                  onClick={() => actionDashboard(searchQuery || [])}
                >
                  Dashboard
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => actionSelect(searchQuery || [])}
                >
                  Devices
                </Button>
              </Grid>

              <Grid item className={classes.summaryItem}>
                Total search results: {devices.length}
              </Grid>
            </Grid>
          )}
          {view}
        </div>
        <Visible when={devices && devices.length > 99}>
          <Typography align="center" component="div" variant="h6">
            Not all matching results have been listed, please refine your search
          </Typography>
        </Visible>
      </div>
    );
  }

  return <div className={classes.noresults}>No results</div>;
};

export default SearchResults;
